import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate, useParams } from "react-router-dom";
import { BASE_URL } from "../config";

const DoctorScheduleUpdate = () => {
  const { id } = useParams(); // Get the doctor ID from the URL
  const navigate = useNavigate();

  // State for holding the schedule data from the API
  const [hours, setHours] = useState({});
  const [loading, setLoading] = useState(true); // For loading state
  const [validationMessages, setValidationMessages] = useState({});

  // Fetch the doctor's schedule data from the API
  useEffect(() => {
    const fetchDoctorSchedule = async () => {
      try {
        const response = await fetch(
          `${BASE_URL}SpecialistDoctorSchedule?Userid=${id}`,
          {
            method: "GET",
            headers: {
              accept: "application/json",
            },
          }
        );
        const data = await response.json();

        if (data && data.getSpecialistScheduleDetail) {
          const updatedHours = {};

          // Map the fetched API data to the state
          data.getSpecialistScheduleDetail.forEach((schedule) => {
            const day = schedule.dayofweek;
            updatedHours[day] = schedule.scheduleTimes.map((timeSlot) => ({
              open: timeSlot.startTime,
              close: timeSlot.endTime,
              closed: timeSlot.status === "Closed",
              status: timeSlot.status === "Closed" ? "Inactive" : "Active",
            }));
          });

          setHours(updatedHours);
        }
      } catch (error) {
        console.error("Error fetching doctor schedule:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDoctorSchedule();
  }, [id]);

  // Handle time change (open and close)
  const handleTimeChange = (day, index, type, value) => {
    const updatedHours = { ...hours };
    updatedHours[day][index][type] = value;
    setHours(updatedHours);
  };

  // Handle adding a new time slot
  const handleAddSlot = (day) => {
    const updatedHours = { ...hours };
    updatedHours[day].push({
      open: "10:00 AM",
      close: "11:59 PM",
      closed: false,
      status: "Active",
    });
    setHours(updatedHours);
  };

  // Handle removing a time slot
  const handleRemoveSlot = (day, index) => {
    const updatedHours = { ...hours };
    updatedHours[day].splice(index, 1);
    setHours(updatedHours);
  };

  // Handle changing the status of the slot (Closed/Active)
  const handleClosedChange = (day, index, checked) => {
    const updatedHours = { ...hours };
    updatedHours[day][index].closed = checked;
    updatedHours[day][index].status = checked ? "Inactive" : "Active";
    setHours(updatedHours);
  };

  // Handle form submission to update the schedule
  const handleSubmit = async (e) => {
    e.preventDefault();
    const scheduleData = Object.keys(hours).map((day) => ({
      dayofweek: day,
      scheduleTimes: hours[day].map((slot) => ({
        startTime: slot.open,
        endTime: slot.close,
        status: slot.closed ? "Closed" : "Active",
      })),
    }));

    try {
      const response = await fetch(
        `${BASE_URL}UpdateDoctorSchedule?Userid=${id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ doctorSchedule: scheduleData }),
        }
      );

      if (response.ok) {
        alert("Schedule updated successfully!");
        navigate(`/DoctorSchedule/${id}`); // Navigate to another page after successful update
      } else {
        alert("Error updating schedule.");
      }
    } catch (error) {
      console.error("Error submitting updated schedule:", error);
      alert("Error updating schedule.");
    }
  };

  return (
    <div className="container my-4">
      <h3 className="text-center mb-4">Update Doctor's Schedule</h3>

      {loading ? (
        <div className="d-flex justify-content-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          {Object.keys(hours).map((day) => (
            <div className="mb-4" key={day}>
              <h5 className="fw-bold">{day}</h5>
              {hours[day]?.map((slot, index) => (
                <div className="row align-items-center mb-2 g-2" key={index}>
                  <div className="col-5 col-md-4">
                    <input
                      type="time"
                      className="form-control"
                      value={slot.open}
                      disabled={slot.closed}
                      onChange={(e) => handleTimeChange(day, index, "open", e.target.value)}
                    />
                  </div>
                  <div className="col-1 text-center">to</div>
                  <div className="col-5 col-md-4">
                    <input
                      type="time"
                      className="form-control"
                      value={slot.close}
                      disabled={slot.closed}
                      onChange={(e) => handleTimeChange(day, index, "close", e.target.value)}
                    />
                  </div>
                  <div className="col-6 col-md-2">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`closed-${day}-${index}`}
                        checked={slot.closed}
                        onChange={(e) => handleClosedChange(day, index, e.target.checked)}
                      />
                      <label htmlFor={`closed-${day}-${index}`} className="form-check-label">
                        Closed
                      </label>
                    </div>
                  </div>
                  {hours[day].length > 1 && (
                    <div className="col-6 col-md-2 text-end">
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => handleRemoveSlot(day, index)}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="btn btn-primary btn-sm mt-2"
                onClick={() => handleAddSlot(day)}
              >
                + Add Slot
              </button>
              <hr />
            </div>
          ))}
          <button type="submit" className="btn btn-success mt-4">
            Save Schedule
          </button>
        </form>
      )}
    </div>
  );
};

export default DoctorScheduleUpdate;
